import {
  ArrowDownTrayIcon,
  XMarkIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import { ChangeEvent, useState } from "react";
import { useMutation } from "react-query";
import { uploadFile } from "../../services/file";
import { createTermsOfUse, updateTerms } from "../../services/termsOfUse";
import { ITermsOfUse } from "@/types/TermsOfUse";

const initialState = {
  name: "",
  version: "",
  date: dayjs().format("YYYY-MM-DD"),
  fileUrl: null,
};

const PolicyUploader = ({
  onClose,
  mode,
  selectedPolicy,
  refetch,
}: {
  onClose: () => void;
  mode: "add" | "edit";
  selectedPolicy?: ITermsOfUse | null;
  refetch(): void;
}) => {
  const [file, setFile] = useState<File>();
  const [state, setState] = useState(
    selectedPolicy
      ? {
          name: "",
          version: selectedPolicy.terms_of_use_version,
          date: dayjs(selectedPolicy.terms_of_use_date).format("YYYY-MM-DD"),
          fileUrl: null,
        }
      : initialState
  );

  const { data, error, isError, isLoading, mutateAsync } = useMutation(
    uploadFile,
    {
      mutationKey: "uploadFile",
    }
  );
  const {
    isError: isPolicyCreateError,
    isLoading: isCreating,
    mutateAsync: createTermsOfUseMutation,
  } = useMutation(createTermsOfUse, {
    mutationKey: "createTermsOfUse",
  });

  const { isLoading: isUpdating, mutateAsync: updatePolicyMutation } =
    useMutation(updateTerms, { mutationKey: "updateTerms" });

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFile(e.target.files[0]);
    }
  };

  const savePolicy = async () => {
    if (mode === "add") {
      if (state.name && state.version) {
        let policyUrl = "";
        if (file) {
          const res = await mutateAsync(file);
          policyUrl = res.url;
          // console.log(res);
        }
        if (file || (!file && state.fileUrl)) {
          await createTermsOfUseMutation({
            terms_of_use_url: file ? policyUrl : state.fileUrl,
            terms_of_use_date: dayjs(state.date).toISOString(),
            terms_of_use_version: state.version,
            terms_of_use_text: state.name
          });
          refetch();
          onClose();
          setState(initialState);
          setFile(undefined);
        }
      }
    } else {
      await updatePolicyMutation({
        terms_of_use_date: dayjs(state.date).toISOString(),
        terms_of_use_version: state.version,
        id: selectedPolicy?.id,        
        terms_of_use_text: state.name
      });
      refetch();
      onClose();
      setState(initialState);
      setFile(undefined);
    }
  };

  const handleChange = (key: keyof typeof state, value: string) => {
    setState({
      ...state,
      [key]: value,
    });
  };

  const renderFileTypeIcon = () => {
    if (file?.type === "docx") {
      return <img src="/images/docsIcon.png" alt="" className="h-6 w-6 mr-2" />;
    }
    return <DocumentTextIcon className="h-6 w-6 mr-2" />;
  };

  return (
    <div className="mb-8">
      <button className="float-right" onClick={() => onClose()}>
        <XMarkIcon className="h-4 w-4 mr-4 mt-4" />
      </button>
      {mode === "add" && (
        <label
          htmlFor="dropzone-file"
          className="block text-center bg-grey-2 p-8"
        >
          <div className="flex justify-center text-blue-1 mb-4">
            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
            <h4>Drag and drop Documents here</h4>
          </div>
          <input
            id="dropzone-file"
            type="file"
            className="hidden"
            onChange={handleFileChange}
          />
          <span className="text-gray-1 block mb-4">OR</span>
          <input
            type="text"
            className="text-center w-96 px-4 py-2"
            onChange={(e) => handleChange("fileUrl", e.target.value)}
            placeholder="Enter URL here"
          />
        </label>
      )}
      <div className="bg-white p-6">
        <div className="flex">
          {renderFileTypeIcon()}
          <div className="flex justify-between ml-2 text-left">
            <div>
              <h3>{file?.name}</h3>
              <h5 className="text-gray-2">
                {state.version} {dayjs().format("MMM DD YYYY")}
              </h5>
            </div>
          </div>
        </div>
        <table className="mt-6">
          <tbody className="text-left">
            <tr>
              <td>
                <label htmlFor="name" className="text-grey-3">
                  Name:
                </label>
              </td>
              <td className="py-2">
                <input
                  id="name"
                  type="text"
                  value={state.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className="w-full bg-grey-4 px-2 py-1 ml-6"
                />
              </td>
            </tr>
            <tr>
              <td>
                <label htmlFor="version" className="text-grey-3">
                  Version:
                </label>
              </td>
              <td className="py-2">
                <input
                  id="version"
                  type="text"
                  value={state.version}
                  onChange={(e) => handleChange("version", e.target.value)}
                  className="w-full bg-grey-4 px-2 py-1 ml-6"
                />
              </td>
            </tr>
            <tr>
              <td>
                <label htmlFor="date" className="text-grey-3">
                  Revision date:
                </label>
              </td>
              <td className="py-2">
                <input
                  id="date"
                  type="date"
                  value={state.date}
                  onChange={(e) => handleChange("date", e.target.value)}
                  className="bg-grey-4 w-full px-2 py-1 ml-6"
                />
              </td>
            </tr>
          </tbody>
        </table>
        <button
          className="bg-blue-1 text-white w-32 mt-6 px-4 py-2"
          onClick={savePolicy}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default PolicyUploader;
